import { createDocumentBase } from '@/storage/grid/documentBase'
import type { ChunkedVoxelGrid } from '@/storage/grid/ChunkedVoxelGrid'
import { parseKeyX, parseKeyY, parseKeyZ, posKeyXYZ, type Position } from '@/voxelMath'
import type { VoxelData } from '@/voxelTypes'
import { buildPackedPreviewStorage, type PackedPreviewTransform } from '@/voxels/packedPreview'
import { hexToHsl, hslToHex } from '@/utils/color'
import { TINT_DEFAULT_COLOR, TINT_VOXEL_TYPE } from '@/voxels/tint/tintConstants'
import { usesSurfaceOrientation } from '@/voxels/registry'

/**
 * Palette swatches as a tiny document (prototype).
 *
 * Every swatch is a few cells of a real voxel type, written into one shared "sheet" grid at its own
 * slot, so it is drawn by exactly the runtimes that draw documents (`ChunkedDocumentFields`) and can
 * never drift from what a stroke paints. A slot is one 16-cell chunk column, far enough apart that
 * an SDF blend or a neighbour-reading type in one slot never reaches the next.
 *
 * The cells of a swatch are the type's preview arrangement (`VoxelSpec.preview` keys: a blob is two
 * touching cells, a road a short run, most types one cell). Its viewing angle is the calibration
 * rotation the grouped previews used (`previewTransform.rotation`), applied to the camera instead of
 * the content. Its scale is not hand-tuned: `SwatchAtlas` measures each shape's silhouette once and
 * fits it, so every type fills its swatch the same way.
 */

export const SWATCH_SLOT_SIZE = 16
export const SWATCH_COLUMNS = 8
export const SWATCH_ROWS = 8
export const SWATCH_SLOT_COUNT = SWATCH_COLUMNS * SWATCH_ROWS
/** Cells of a slot's arrangement start here, leaving room for SDF blur on every side. */
const SLOT_INSET: Position = { x: 6, y: 4, z: 6 }

export const SWATCH_SHEET_BOUNDS: Position = {
  x: SWATCH_COLUMNS * SWATCH_SLOT_SIZE,
  y: SWATCH_SLOT_SIZE,
  z: SWATCH_ROWS * SWATCH_SLOT_SIZE,
}

export type SwatchItem = Pick<VoxelData, 'type' | 'color' | 'material' | 'face'>

export function swatchKey(item: SwatchItem): string {
  const material = item.material ? JSON.stringify(item.material) : ''
  return `${item.type}|${item.color ?? ''}|${item.face ?? ''}|${material}`
}

/**
 * Per-type swatch overrides (prototype; a product version would be a `VoxelSpec.preview` field).
 * Most types need none: their preview arrangement, measured fit and calibration rotation are enough.
 */
export type SwatchOverride = {
  /** The swatch's cells (relative), replacing the preview arrangement. */
  cells?: Array<[x: number, y: number, z: number]>
  /**
   * A small staged scene instead: cells with their own values (e.g. several colours of the item's
   * type). Wins over `cells`.
   */
  build?: (item: SwatchItem) => SwatchCell[]
  /**
   * A white solid support under each cell, placed exactly behind it in the swatch's view so it is
   * never seen: transmissive types (water, ice) refract it instead of an empty background, which
   * otherwise leaves a dark body and a faint full-frame square. 'behind' = z - 1 (face-on views),
   * 'below' = y - 1 (top views).
   */
  backing?: 'behind' | 'below'
  /** Silhouette size relative to `SWATCH_FILL` (1 = default; < 1 for shapes that read large). */
  fill?: number
  /** Viewing angle (Euler XYZ, as `previewTransform.rotation`), replacing the type's calibration. */
  rotation?: [number, number, number]
}

/** A swatch cell (relative), optionally with its own value (default: the item's voxel). */
export type SwatchCell = [x: number, y: number, z: number, value?: VoxelData]

function shiftHue(color: number, degrees: number): number {
  const { h, s, l } = hexToHsl(color)
  return hslToHex((((h + degrees) % 360) + 360) % 360, s, l)
}

/**
 * Tint is used in layers of different colours that tint each other (stained glass), so its swatch is a
 * 3 x 3 x 3 cube of three layers: the item's colour on top, the same colour turned 120 and 240 degrees
 * round the hue circle below it.
 */
/** HSL saturation (0-100) below which a colour counts as grey. */
const MIN_LAYER_SATURATION = 15

function layeredTintCube(item: SwatchItem): SwatchCell[] {
  // A near-grey colour has no hue to turn; such swatches show the default ink's layers instead.
  const top = item.color != null && hexToHsl(item.color).s >= MIN_LAYER_SATURATION ? item.color : TINT_DEFAULT_COLOR
  const layers = [shiftHue(top, 240), shiftHue(top, 120), top]
  const out: SwatchCell[] = []
  for (let y = 0; y < 3; y++) {
    for (let x = 0; x < 3; x++) for (let z = 0; z < 3; z++) out.push([x, y, z, { type: item.type, color: layers[y] }])
  }
  return out
}

/**
 * Staged scenes drawn as swatches (prototype; the home lab's theme tiles): an item
 * `{ type: SCENE_SWATCH_TYPE, color: key }` draws the cells registered under `key`. Scenes that
 * share one layout (only their colours differ) share one measured fit, like colours of one type;
 * scenes registered with different `layout` names are measured apart.
 */
export const SCENE_SWATCH_TYPE = 'swatch_scene'
const sceneSwatches = new Map<number, SwatchCell[]>()
const sceneLayouts = new Map<number, string>()
export function registerSceneSwatch(key: number, cells: SwatchCell[], layout = ''): void {
  sceneSwatches.set(key, cells)
  sceneLayouts.set(key, layout)
}

export const SWATCH_OVERRIDES: Readonly<Record<string, SwatchOverride>> = {
  // Seen from above and to one side, like a tiny world.
  [SCENE_SWATCH_TYPE]: { build: (item) => sceneSwatches.get(item.color ?? 0) ?? [[0, 0, 0]], rotation: [0.62, -0.62, 0] },
  [TINT_VOXEL_TYPE]: { build: layeredTintCube },
  // A lone tube cell has no neighbour to run to and meshes as a ball; two cells show a tube.
  tube: { cells: [[0, 0, 0], [1, 0, 0]] },
  // Round or pointed shapes fill their box less than a cube does, so at equal fit they read large.
  tree: { fill: 0.82 },
  heart: { fill: 0.82 },
  sphere: { fill: 0.8 },
  googly_eye: { fill: 0.74 },
  // Seen face-on (ice) or from the top (water), these are flat squares, which read larger than a
  // turned cube at the same fit.
  ice_cube: { fill: 0.74 },
  water: { fill: 0.68 },
}

/** The swatch's cells relative to its arrangement origin, its hidden support, and its calibration. */
export type SwatchArrangement = {
  cells: SwatchCell[]
  /** Cells of the white solid support (see `SwatchOverride.backing`). */
  backing: Array<[x: number, y: number, z: number]>
  transform: PackedPreviewTransform | undefined
  fill: number
  rotation?: [number, number, number]
}

export function swatchArrangement(item: SwatchItem): SwatchArrangement {
  const override = SWATCH_OVERRIDES[item.type]
  const preview = buildPackedPreviewStorage(item.type, (item.color ?? 0xffffff) >>> 0)
  const keys = preview?.keys && preview.keys.length > 0 ? preview.keys : [posKeyXYZ(0, 0, 0)]
  const cells: SwatchCell[] = override?.build?.(item)
    ?? override?.cells
    ?? keys.map((k): SwatchCell => [parseKeyX(k), parseKeyY(k), parseKeyZ(k)])
  const backing = override?.backing === 'behind'
    ? cells.map(([x, y, z]): [number, number, number] => [x, y, z - 1])
    : override?.backing === 'below'
      ? cells.map(([x, y, z]): [number, number, number] => [x, y - 1, z])
      : []
  return { cells, backing, transform: preview?.previewTransform, fill: override?.fill ?? 1, rotation: override?.rotation }
}

const BACKING: VoxelData = { type: 'solid', color: 0xf4f4f2 }

function slotOrigin(slot: number): Position {
  return {
    x: (slot % SWATCH_COLUMNS) * SWATCH_SLOT_SIZE + SLOT_INSET.x,
    y: SLOT_INSET.y,
    z: Math.floor(slot / SWATCH_COLUMNS) * SWATCH_SLOT_SIZE + SLOT_INSET.z,
  }
}

/** Where a camera must look to frame one slot, before the silhouette fit. */
export type SwatchFraming = {
  /** Centre of the slot's cells (world). */
  target: Position
  /** Largest side of the slot's cell box, in cells. */
  span: number
  /** Euler XYZ rotation the grouped preview applied to the content; the camera applies its inverse. */
  rotation: [number, number, number]
  /** What the silhouette depends on (not the colour): the fit is measured once per shape key. */
  shapeKey: string
  /** `SwatchOverride.fill` (1 = default). */
  fill: number
}

/** The silhouette depends on type, material, face and arrangement, never on colour. */
export function swatchShapeKey(item: SwatchItem): string {
  const material = item.material ? JSON.stringify(item.material) : ''
  const layout = item.type === SCENE_SWATCH_TYPE ? sceneLayouts.get(item.color ?? 0) ?? '' : ''
  return `${item.type}|${item.face ?? ''}|${material}|${layout}`
}

/** Grouped previews without a calibration were drawn face-on. */
const DEFAULT_ROTATION: [number, number, number] = [0, 0, 0]
/**
 * Side of a swatch image, in palette units (the item box the grouped previews drew into). The UI
 * draws the image this large, centred on the item.
 */
export const SWATCH_IMAGE_OVERSCAN = 1.5
/** Largest side of the measured silhouette, as a fraction of the image side (the rest is margin). */
export const SWATCH_FILL = 0.86

/**
 * The persistent sheet: one grid over `SWATCH_SHEET_BOUNDS`, a key per slot, and the framing of each
 * occupied slot. Edits go through `asMutable` / `asImmutable`, so the palette lineage is kept and the
 * runtimes remesh only the chunks of changed slots.
 */
export class SwatchSheet {
  private grid: ChunkedVoxelGrid = createDocumentBase(undefined, undefined, SWATCH_SHEET_BOUNDS)
  private readonly slotKeys: Array<string | null> = new Array(SWATCH_SLOT_COUNT).fill(null)
  private readonly slotCells: Array<number[]> = Array.from({ length: SWATCH_SLOT_COUNT }, () => [])
  private readonly framings: Array<SwatchFraming | null> = new Array(SWATCH_SLOT_COUNT).fill(null)
  /** Slots in least-recently-used order (front = oldest). */
  private readonly lru: number[] = Array.from({ length: SWATCH_SLOT_COUNT }, (_, i) => i)

  current(): ChunkedVoxelGrid {
    return this.grid
  }

  framing(slot: number): SwatchFraming | null {
    return this.framings[slot]
  }

  slotOf(key: string): number {
    return this.slotKeys.indexOf(key)
  }

  /**
   * Places each item in a slot (reusing the slot already holding its key, else the least recently
   * used one) and returns the slots in item order. One grid edit for the whole batch.
   */
  place(items: readonly SwatchItem[]): number[] {
    const slots: number[] = []
    const pinned = new Set<number>()
    let next: ChunkedVoxelGrid | null = null
    for (const item of items) {
      const key = swatchKey(item)
      let slot = this.slotKeys.indexOf(key)
      if (slot < 0) {
        slot = this.lru.find((s) => !pinned.has(s)) ?? 0
        next ??= this.grid.asMutable()
        this.write(next, slot, key, item)
      }
      pinned.add(slot)
      this.touch(slot)
      slots.push(slot)
    }
    if (next) this.grid = next.asImmutable()
    return slots
  }

  private write(grid: ChunkedVoxelGrid, slot: number, key: string, item: SwatchItem): void {
    for (const k of this.slotCells[slot]) grid.delete(k)
    const origin = slotOrigin(slot)
    const { cells, backing, transform, fill, rotation } = swatchArrangement(item)
    // Surface-oriented types (googly eyes) face the camera (+z).
    const face = item.face ?? (usesSurfaceOrientation(item.type) ? 'z+' : undefined)
    const value: VoxelData = { type: item.type, color: item.color, ...(face ? { face } : {}), ...(item.material ? { material: item.material } : {}) }
    const written: number[] = []
    let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity
    for (const [x, y, z, own] of cells) {
      const k = posKeyXYZ(origin.x + x, origin.y + y, origin.z + z)
      grid.set(k, own ?? value)
      written.push(k)
      minX = Math.min(minX, x); minY = Math.min(minY, y); minZ = Math.min(minZ, z)
      maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); maxZ = Math.max(maxZ, z)
    }
    for (const [x, y, z] of backing) {
      const k = posKeyXYZ(origin.x + x, origin.y + y, origin.z + z)
      if (grid.get(k) === undefined) grid.set(k, BACKING)
      written.push(k)
    }
    const span = Math.max(maxX - minX + 1, maxY - minY + 1, maxZ - minZ + 1)
    const center: Position = {
      x: origin.x + (minX + maxX + 1) / 2,
      y: origin.y + (minY + maxY + 1) / 2,
      z: origin.z + (minZ + maxZ + 1) / 2,
    }
    this.slotKeys[slot] = key
    this.slotCells[slot] = written
    this.framings[slot] = {
      target: center,
      span,
      rotation: rotation ?? transform?.rotation ?? DEFAULT_ROTATION,
      shapeKey: swatchShapeKey(item),
      fill,
    }
  }

  private touch(slot: number): void {
    const i = this.lru.indexOf(slot)
    if (i >= 0) this.lru.splice(i, 1)
    this.lru.push(slot)
  }
}
